import AppLayout from '../components/layout/AppLayout';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiClipboard, FiFileText, FiAlertTriangle } from 'react-icons/fi';
import { ROUTES } from '../constants/constants';
import { aiAPI } from '../services/api';
import { useUI } from '../context/UIContext';
import Button from '../components/common/Button';
import { Icon } from '../components/common/Icon';

const CHECKLIST = [
  { item: 'Privacy Policy (DPDP aligned)',       status: 'done',    priority: 'critical' },
  { item: 'Consent Management System',           status: 'pending', priority: 'critical' },
  { item: 'Data Audit & Inventory',              status: 'inprog',  priority: 'high' },
  { item: 'Breach Response Plan',                status: 'pending', priority: 'critical' },
  { item: 'Data Processing Agreements',          status: 'done',    priority: 'high' },
  { item: 'Employee Data Privacy Training',      status: 'pending', priority: 'medium' },
  { item: 'Data Minimisation Review',            status: 'inprog',  priority: 'high' },
  { item: 'Significant Data Fiduciary Check',    status: 'done',    priority: 'medium' },
];

const PENALTIES = [
  { title: 'Data Breach / Security Failure', desc: 'Failure to implement reasonable security safeguards',  amount: '₹250 Cr', priority: 'critical' },
  { title: 'Child Data Violation',           desc: 'Processing child data without parental consent',        amount: '₹200 Cr', priority: 'critical' },
  { title: 'Data Principal Rights Failure',  desc: 'Failing to honour user rights (access, erasure)',      amount: '₹50 Cr',  priority: 'warn' },
  { title: 'Breach Notice Failure',          desc: 'Not notifying DPB of personal data breach',           amount: '₹200 Cr', priority: 'critical' },
];

const STATUS_STYLES = {
  done:    { label: 'Done',        bg: 'bg-[#e8efe5]', text: 'text-[#4a6b3f]' },
  pending: { label: 'Pending',     bg: 'bg-[#f3ebd7]', text: 'text-[#8a6a1f]' },
  inprog:  { label: 'In Progress', bg: 'bg-[#edeef3]', text: 'text-[#3a4a66]' },
};

const PRIORITY_META = {
  critical: { color: '#9a564a', label: 'Critical' },
  high: { color: '#b07a1a', label: 'High' },
  medium: { color: '#8a6a1f', label: 'Medium' },
};

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { showToast } = useUI();

  const handleDraftDoc = async (type) => {
    const target = ROUTES.AGENT;
    const targetLabel = 'AI Agent';
    try {
      await aiAPI.draftDocument(type);
      showToast(`Drafting ${type} — check ${targetLabel}`, 'info');
      navigate(target);
    } catch {
      showToast(`Open ${targetLabel} to continue drafting`, 'info');
      navigate(target);
    }
  };

  return (
    <AppLayout pageTitle="Data Privacy">
      <div className="text-[24px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Data Privacy &amp; DPDP Compliance
      </div>
      <div className="text-[13px] text-gray-500 mb-6">
        Digital Personal Data Protection Act, 2023 · IT Act 2000 · GDPR Alignment · All entity types
      </div>

      {/* Hero */}
      <div className="rounded-2xl border border-gray-200 p-6 mb-6" style={{ background: 'linear-gradient(135deg, #f0faf9, #e4f5f3)', boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
        <div className="flex items-center gap-2 text-[17px] font-bold text-teal-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <FiLock size={16} /> DPDP Act 2023 — India's Landmark Data Law
        </div>
        <div className="text-[12px] text-teal-600 mb-4">Effective compliance framework for companies handling personal data of Indian citizens</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: 'consent',   title: 'Consent Framework',          desc: 'Valid, free, specific, informed & unconditional consent required before data processing' },
            { icon: 'fiduciary', title: 'Data Fiduciary Obligations', desc: 'Purpose limitation, data minimisation, storage restriction, accuracy maintenance' },
            { icon: 'rights',    title: 'Data Principal Rights',      desc: 'Right to access, correction, erasure, grievance redressal, nominee rights' },
            { icon: 'global',    title: 'Cross-Border Transfer',      desc: 'Govt notified countries permitted — restricted transfers under Sec 16' },
            { icon: 'warning',   title: 'Breach Notification',       desc: 'Notify Data Protection Board & affected individuals without delay' },
            { icon: 'penalty',   title: 'Penalties',                 desc: 'Up to ₹250 Crore per breach instance — significant financial risk' },
          ].map((f) => (
            <div key={f.title} className="flex gap-2.5 bg-white rounded-xl p-3 border border-teal-100">
              <Icon name={f.icon} size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#145f5f' }} />
              <div>
                <div className="text-[12px] font-bold text-teal-800">{f.title}</div>
                <div className="text-[11px] text-teal-600 mt-0.5 leading-relaxed">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* Checklist */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <span className="flex-1 inline-flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}><FiClipboard size={15} /> DPDP Compliance Checklist</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">AI</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#f8f5ef]">
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[1px] text-gray-500 font-bold">#</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[1px] text-gray-500 font-bold">Requirement</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[1px] text-gray-500 font-bold">Status</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[1px] text-gray-500 font-bold">Priority</th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST.map((row, i) => {
                  const s = STATUS_STYLES[row.status];
                  return (
                    <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-[11.5px] text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-700">{row.item}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-gray-600">
                        {(() => {
                          const pm = PRIORITY_META[row.priority] || {};
                          return (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pm.color }} />
                              {pm.label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Penalties */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <div className="px-5 py-3.5 border-b border-gray-100">
              <span className="inline-flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}><FiAlertTriangle size={15} /> Key Penalties</span>
            </div>
            <div className="p-4 flex flex-col gap-2">
              {PENALTIES.map((p) => (
                <div key={p.title} className={`flex items-start gap-3 px-3.5 py-3 rounded-xl border ${p.priority === 'critical' ? 'bg-[#f2e9e6] border-[#e6d2cb]' : 'bg-[#f3ebd7] border-[#e7d7ad]'}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${p.priority === 'critical' ? 'bg-[#9a564a]' : 'bg-[#b07a1a]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[12.5px] text-gray-800">{p.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{p.desc}</div>
                  </div>
                  <div className={`font-bold text-[12px] whitespace-nowrap ${p.priority === 'critical' ? 'text-[#9a564a]' : 'text-[#8a6a1f]'}`}>{p.amount}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Privacy Documents */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <div className="px-5 py-3.5 border-b border-gray-100">
              <span className="inline-flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}><FiFileText size={15} /> Privacy Documents</span>
            </div>
            <div className="p-4 flex flex-col gap-2">
              {[
                { label: 'Draft Privacy Policy (DPDP)', icon: <FiLock size={15} />,          type: 'DPDP Privacy Policy', style: 'primary' },
                { label: 'Draft Consent Form',          icon: <FiClipboard size={15} />,     type: 'DPDP Consent Form',   style: 'outline' },
                { label: 'Data Processing Agreement',   icon: <FiFileText size={15} />,      type: 'Data Processing Agreement', style: 'outline' },
                { label: 'Breach Response Plan',        icon: <FiAlertTriangle size={15} />, type: 'Data Breach Response Plan', style: 'outline' },
              ].map((btn) => (
                <Button
                  key={btn.type}
                  variant={btn.style}
                  block
                  leftIcon={btn.icon}
                  onClick={() => handleDraftDoc(btn.type)}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
